import { useEffect, useState, type FunctionComponent } from "react";
import NavHomeDashboardAdminPartial from "../home/home/nav-home-dashboard.admin.partials";
import StatsHomeDashboardAdminPartial from "../home/home/stats-home-dashboard.admin.partials";
import { useGetGlobalMetricsAdminHttp } from "../../../hooks/http/admin/campaigns/get-global-metrics.admin.http";
import {
	getBarColor,
	getDateRange,
	handleHttpError,
} from "../../../utils/utils";
import { useToast } from "../../../hooks/use-toast";
import type { ICampaignMetricMinimal } from "../../../types/models/campaign-metric.types";
import Spinner from "../../../components/spinner.components";
import { Barchart } from "../../../components/charts/bar.chart";
import { Linechart } from "../../../components/charts/line.chart";
import millify from "millify";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import ArrowBottomIcon from "../../../assets/svgs/common/arrow-bottom.svg";
import { useNavigate } from "react-router-dom";

const MetricsAdminPartial: FunctionComponent = () => {
	const { execute: getGlobalMetricsHttp, fetching: fetchingGlobalMetrics } =
		useGetGlobalMetricsAdminHttp();
	const [globalMetrics, setGlobalMetrics] = useState<ICampaignMetricMinimal>();
	const [range, setRange] = useState("Last 7 days");
	const navigate = useNavigate();
	const { toast } = useToast();

	async function getGlobalMetrics() {
		const { startDate, endDate } = getDateRange(range);

		const response = await getGlobalMetricsHttp({
			body: null,
			queryParams: {
				startDate,
				endDate,
			},
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setGlobalMetrics(response.data.metrics);
	}

	const AverageViewsPerPostChartData = [
		{
			category: "Views",
			value: globalMetrics?.averageViewsPerPost ?? 0,
			fill: getBarColor(1),
		},
		{
			category: "Likes",
			value: globalMetrics?.averageLikesPerPost ?? 0,
			fill: getBarColor(2),
		},
		{
			category: "Comments",
			value: globalMetrics?.averageCommentsPerPost ?? 0,
			fill: getBarColor(0),
		},
		{
			category: "Shares",
			value: globalMetrics?.averageSharesPerPost ?? 0,
			fill: getBarColor(3),
		},
	];

	const ApprovedPostsChartData = [
		{
			category: "Total Posts",
			value: globalMetrics?.totalPosts ?? 0,
			fill: getBarColor(0),
		},
		{
			category: "Approved Posts",
			value: globalMetrics?.approvedPosts ?? 0,
			fill: getBarColor(1),
		},
		{
			category: "Rejected Posts",
			value: globalMetrics?.rejectedPosts ?? 0,
			fill: getBarColor(2),
		},
	];
	const PostPerDayChartData = [
		{
			category: "Total Posts",
			value: millify(globalMetrics?.totalPosts ?? 0),
		},
	];

	const TotalViewsChartData = [
		{
			category: "Total views",
			value: millify(globalMetrics?.totalViews ?? 0),
		},
		{
			category: "Cost per view",
			value: `₹ ${(globalMetrics?.costPerView ?? 0).toFixed(3)}`,
		},
	];

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		getGlobalMetrics();
	}, [range]);

	return (
		<div className="flex flex-col px-8 pt-16 pb-10">
			<div className="flex flex-col gap-8 w-full">
				<NavHomeDashboardAdminPartial
					buttonName="Create campaign"
					onClickFunction={() => {
						navigate("/admin/brands");
					}}
				/>
				<div className="">
					<DropdownMenu>
						<DropdownMenuTrigger className="flex justify-between items-center bg-tertiarybuttonbg px-5 py-3 border border-border rounded-lg w-40 font-semibold text-sm">
							{range}
							<ArrowBottomIcon />
						</DropdownMenuTrigger>
						<DropdownMenuContent className="bg-background w-40">
							{[
								{
									title: "Last 7 days",
								},
								{
									title: "Last 30 days",
								},
								{
									title: "Last 90 days",
								},
								{
									title: "Last 1 year",
								},
								{
									title: "This month",
								},
								{
									title: "All time",
								},
							].map((item, index) => (
								// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
								<div
									key={item.title}
									onClick={() => setRange(item.title)}
									className="w-full"
								>
									<DropdownMenuItem
										className="flex justify-end items-center p-3 w-full text-sm"
										textValue={item.title}
									>
										{item.title}
									</DropdownMenuItem>
									{/* biome-ignore lint/correctness/useJsxKeyInIterable: <explanation> */}
									<DropdownMenuSeparator
										className={`bg-border  ${index === 5 ? "hidden" : "flex"}`}
									/>
								</div>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				{fetchingGlobalMetrics ? (
					<div className="flex justify-center items-center w-full h-96">
						<Spinner width={25} />
					</div>
				) : (
					globalMetrics && (
						<div className="flex flex-col gap-8 w-full">
							<StatsHomeDashboardAdminPartial
								campaignMetrics={globalMetrics as ICampaignMetricMinimal}
							/>
							<Linechart
								fetchingCampaignMetrics={fetchingGlobalMetrics}
								campaignMetrics={globalMetrics as ICampaignMetricMinimal}
								lineData={globalMetrics.totalViewsChart}
								chartData={TotalViewsChartData}
								title="Total views"
							/>
							<Barchart
								fetchingCampaignMetrics={fetchingGlobalMetrics}
								campaignMetrics={globalMetrics as ICampaignMetricMinimal}
								chartData={AverageViewsPerPostChartData}
								title="Average views per post"
							/>
							<Linechart
								fetchingCampaignMetrics={fetchingGlobalMetrics}
								campaignMetrics={globalMetrics as ICampaignMetricMinimal}
								lineData={globalMetrics.postsPerDay}
								chartData={PostPerDayChartData}
								title="Posts per day"
							/>
							<Barchart
								fetchingCampaignMetrics={fetchingGlobalMetrics}
								campaignMetrics={globalMetrics as ICampaignMetricMinimal}
								chartData={ApprovedPostsChartData}
								title="Approved posts"
							/>
						</div>
					)
				)}
			</div>
		</div>
	);
};

export default MetricsAdminPartial;
